import { getSession } from '../session.js';
import type { Transaction, Order } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export async function renderBusinessAnalytics(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const businessId = session?.businessId ?? 'preview';
  const industry = session?.industry ?? 'general';
  const memberSince = new Date(); // would come from business record

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title"><i class="bi bi-graph-up"></i> Business Analytics</h1>
          <p class="page-subtitle">Performance, compliance activity, and savings overview.</p>
        </div>
        <div style="display:flex;gap:8px">
          ${['7d','30d','all'].map((p, i) => `<button class="filter-chip ${i === 0 ? 'active' : ''}" data-period="${p}">${p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : 'All Time'}</button>`).join('')}
        </div>
      </div>
    </div>

    <!-- Top stats -->
    <div class="stats-grid" style="margin-bottom:24px" id="analytics-stats">
      <div class="stat-card"><div class="stat-value" id="an-revenue">—</div><div class="stat-label"><i class="bi bi-cash-coin"></i> Revenue Tracked</div></div>
      <div class="stat-card"><div class="stat-value" id="an-docs">—</div><div class="stat-label"><i class="bi bi-file-earmark-check"></i> Documents Certified</div></div>
      <div class="stat-card"><div class="stat-value" id="an-sessions">—</div><div class="stat-label"><i class="bi bi-robot"></i> AI Sessions</div></div>
      <div class="stat-card"><div class="stat-value" id="an-saving" style="color:#10b981">—</div><div class="stat-label"><i class="bi bi-piggy-bank"></i> Est. Time Saved</div></div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
      <!-- Revenue chart -->
      <div class="card">
        <div style="font-size:13px;font-weight:700;margin-bottom:16px"><i class="bi bi-bar-chart-fill" style="color:var(--brand-gold)"></i> Revenue Over Time</div>
        <div id="revenue-bars" style="display:flex;align-items:flex-end;gap:6px;height:120px;padding-bottom:4px"></div>
        <div id="revenue-labels" style="display:flex;gap:6px;margin-top:6px;font-size:10px;color:var(--text-muted)"></div>
      </div>

      <!-- Activity breakdown -->
      <div class="card">
        <div style="font-size:13px;font-weight:700;margin-bottom:16px"><i class="bi bi-pie-chart-fill" style="color:var(--brand-gold)"></i> Activity Breakdown</div>
        <div id="activity-list" style="display:flex;flex-direction:column;gap:10px"></div>
      </div>
    </div>

    <!-- Compliance score -->
    <div class="card" style="margin-bottom:20px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <div style="font-size:13px;font-weight:700"><i class="bi bi-shield-check" style="color:var(--success)"></i> Compliance Score</div>
        <button class="btn btn-ghost btn-sm" id="go-compliance"><i class="bi bi-arrow-right"></i> View Details</button>
      </div>
      <div style="display:flex;align-items:center;gap:24px;flex-wrap:wrap">
        <div style="text-align:center;flex-shrink:0">
          <div style="font-size:48px;font-weight:900;color:var(--success)" id="compliance-pct">—</div>
          <div style="font-size:11px;color:var(--text-muted)">Compliance Score</div>
        </div>
        <div style="flex:1;min-width:0" id="compliance-items"></div>
      </div>
    </div>

    <!-- Recent activity feed -->
    <div class="card">
      <div style="font-size:13px;font-weight:700;margin-bottom:16px"><i class="bi bi-activity" style="color:var(--brand-gold)"></i> Recent Activity</div>
      <div id="activity-feed">
        <div style="text-align:center;padding:20px"><div class="spinner" style="margin:0 auto"></div></div>
      </div>
    </div>
  `;

  document.getElementById('go-compliance')!.addEventListener('click', () => navigate('compliance'));

  document.querySelectorAll('[data-period]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      document.querySelectorAll('[data-period]').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      await loadAnalytics(businessId, industry, btn.getAttribute('data-period') as string, navigate);
    });
  });

  await loadAnalytics(businessId, industry, '7d', navigate);
}

async function loadAnalytics(businessId: string, industry: string, period: string, navigate: NavigateFn): Promise<void> {
  let transactions: Transaction[] = [];
  let orders: Order[] = [];
  const since = periodToSince(period);

  try {
    const api = getAPI();
    [transactions, orders] = await Promise.all([
      api.getTransactions(businessId, since) as Promise<Transaction[]>,
      api.getOrders(businessId) as Promise<Order[]>,
    ]);
  } catch {
    transactions = getSampleTransactions(businessId, since);
    orders = getSampleOrders(businessId, since);
  }

  const revenue = transactions.reduce((s, t) => s + t.amount + (t.tip ?? 0), 0);
  const certifiedDocs = Math.floor(Math.random() * 8) + 2; // simulated
  const aiSessions = Math.floor(Math.random() * 15) + 3;
  const timeSavedHrs = (certifiedDocs * 2.5 + aiSessions * 1.2).toFixed(1);

  (document.getElementById('an-revenue') as HTMLElement).textContent = `$${revenue.toFixed(2)}`;
  (document.getElementById('an-docs') as HTMLElement).textContent = String(certifiedDocs);
  (document.getElementById('an-sessions') as HTMLElement).textContent = String(aiSessions);
  (document.getElementById('an-saving') as HTMLElement).textContent = `${timeSavedHrs}hrs`;

  renderRevenueBars(transactions);
  renderActivityBreakdown(industry, orders, certifiedDocs, aiSessions);
  renderComplianceScore(industry);
  renderActivityFeed(transactions, orders, certifiedDocs);
}

function renderRevenueBars(transactions: Transaction[]): void {
  const barsEl = document.getElementById('revenue-bars')!;
  const labelsEl = document.getElementById('revenue-labels')!;
  const days = 7;
  const buckets: number[] = Array(days).fill(0);
  const labels: string[] = [];
  const now = Date.now();

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now - i * 86400000);
    labels.push(d.toLocaleDateString(undefined, { weekday: 'short' }));
    const dayStart = new Date(d); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(d); dayEnd.setHours(23, 59, 59, 999);
    buckets[days - 1 - i] = transactions
      .filter((t) => new Date(t.createdAt) >= dayStart && new Date(t.createdAt) <= dayEnd)
      .reduce((s, t) => s + t.amount, 0);
  }

  const maxVal = Math.max(...buckets, 1);
  barsEl.innerHTML = buckets.map((v, i) => {
    const pct = Math.round((v / maxVal) * 100);
    return `
      <div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:4px">
        <div style="font-size:10px;color:var(--text-muted)">${v > 0 ? `$${v.toFixed(0)}` : ''}</div>
        <div style="flex:1;display:flex;align-items:flex-end;width:100%">
          <div style="width:100%;background:${i === buckets.length - 1 ? 'var(--brand-gold)' : 'var(--brand-blue-light)'};border-radius:4px 4px 0 0;height:${Math.max(pct, 4)}%;min-height:4px;transition:height .3s"></div>
        </div>
      </div>
    `;
  }).join('');
  labelsEl.innerHTML = labels.map((l) => `<div style="flex:1;text-align:center">${l}</div>`).join('');
}

function renderActivityBreakdown(industry: string, orders: Order[], docs: number, sessions: number): void {
  const items = [
    { label: 'AI Sessions', value: sessions, color: '#3b82f6', icon: 'bi-robot' },
    { label: 'Documents Certified', value: docs, color: '#10b981', icon: 'bi-file-earmark-check' },
    { label: 'Orders Processed', value: orders.length, color: '#f59e0b', icon: 'bi-receipt' },
    { label: 'Compliance Checks', value: Math.floor(sessions * 0.7), color: '#8b5cf6', icon: 'bi-shield-check' },
  ].filter((i) => industry !== 'construction' || i.label !== 'Orders Processed');

  const maxVal = Math.max(...items.map((i) => i.value), 1);
  const el = document.getElementById('activity-list')!;
  el.innerHTML = items.map((item) => `
    <div>
      <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:3px">
        <span style="color:var(--text-secondary)"><i class="bi ${item.icon}" style="color:${item.color}"></i> ${item.label}</span>
        <span style="font-weight:700">${item.value}</span>
      </div>
      <div style="background:var(--bg-tertiary);border-radius:4px;height:6px">
        <div style="height:100%;background:${item.color};width:${(item.value / maxVal) * 100}%;border-radius:4px"></div>
      </div>
    </div>
  `).join('');
}

function renderComplianceScore(industry: string): void {
  const items = [
    { label: 'Wage Compliance', done: true },
    { label: 'Document Certification', done: true },
    { label: 'Audit Trail Active', done: true },
    { label: 'Employee Records', done: industry === 'restaurant', industries: ['restaurant'] },
    { label: 'Tip Pool Documentation', done: industry === 'restaurant', industries: ['restaurant'] },
    { label: 'Contractor Registration', done: industry === 'construction', industries: ['construction'] },
    { label: 'Prevailing Wage Filed', done: industry === 'construction', industries: ['construction'] },
  ].filter((i) => !i.industries || i.industries.includes(industry));

  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / items.length) * 100);

  (document.getElementById('compliance-pct') as HTMLElement).textContent = `${pct}%`;
  (document.getElementById('compliance-pct') as HTMLElement).style.color = pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';

  document.getElementById('compliance-items')!.innerHTML = items.map((item) => `
    <div style="display:flex;align-items:center;gap:8px;font-size:12px;margin-bottom:6px">
      <i class="bi ${item.done ? 'bi-check-circle-fill' : 'bi-circle'}" style="color:${item.done ? '#10b981' : 'var(--text-muted)'}"></i>
      <span style="color:${item.done ? 'var(--text-primary)' : 'var(--text-muted)'}">${item.label}</span>
    </div>
  `).join('');
}

function renderActivityFeed(transactions: Transaction[], orders: Order[], docs: number): void {
  const feed = document.getElementById('activity-feed')!;
  const events = [
    ...transactions.slice(0, 5).map((t) => ({
      icon: 'bi-cash-coin', color: '#10b981',
      text: `Payment processed — $${t.amount.toFixed(2)}${t.customerName ? ` (${t.customerName})` : ''}`,
      time: t.createdAt,
    })),
    ...orders.slice(0, 3).map((o) => ({
      icon: 'bi-receipt', color: '#f59e0b',
      text: `Order placed — Table ${o.tableNumber} · $${o.total.toFixed(2)}`,
      time: o.createdAt,
    })),
    { icon: 'bi-file-earmark-check', color: '#3b82f6', text: 'Document certified via AI audit trail', time: new Date(Date.now() - 3600000).toISOString() },
    { icon: 'bi-shield-check', color: '#8b5cf6', text: 'Compliance check passed — WA State', time: new Date(Date.now() - 86400000).toISOString() },
  ].sort((a, b) => b.time.localeCompare(a.time)).slice(0, 10);

  feed.innerHTML = events.length === 0
    ? `<div style="text-align:center;color:var(--text-muted);padding:20px">No activity yet.</div>`
    : events.map((ev) => `
        <div style="display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid var(--border)">
          <div style="width:32px;height:32px;border-radius:50%;background:${ev.color}22;display:flex;align-items:center;justify-content:center;flex-shrink:0">
            <i class="bi ${ev.icon}" style="color:${ev.color};font-size:14px"></i>
          </div>
          <div style="flex:1;min-width:0">
            <div style="font-size:12px;font-weight:500">${escHtml(ev.text)}</div>
            <div style="font-size:10px;color:var(--text-muted);margin-top:2px">${new Date(ev.time).toLocaleString()}</div>
          </div>
        </div>
      `).join('');
}

function periodToSince(period: string): string | undefined {
  if (period === '7d') return new Date(Date.now() - 7 * 86400000).toISOString();
  if (period === '30d') return new Date(Date.now() - 30 * 86400000).toISOString();
  return undefined;
}

function getSampleTransactions(businessId: string, since?: string) {
  const now = new Date();
  const ago = (h: number) => new Date(now.getTime() - h * 3600000).toISOString();
  const all = [
    { id: 'tx1', businessId, amount: 89.50, tip: 15, paymentMethod: 'card' as const, items: [], customerName: 'Sarah M.', createdAt: ago(2) },
    { id: 'tx2', businessId, amount: 42.00, tip: 8, paymentMethod: 'cash' as const, items: [], createdAt: ago(5) },
    { id: 'tx3', businessId, amount: 67.25, paymentMethod: 'card' as const, items: [], customerName: 'James R.', createdAt: ago(26) },
    { id: 'tx4', businessId, amount: 122.80, tip: 20, paymentMethod: 'card' as const, items: [], customerName: 'Emily C.', createdAt: ago(48) },
    { id: 'tx5', businessId, amount: 38.99, paymentMethod: 'cash' as const, items: [], createdAt: ago(72) },
  ];
  return since ? all.filter((t) => t.createdAt >= since) : all;
}

function getSampleOrders(businessId: string, since?: string) {
  const now = new Date();
  const ago = (h: number) => new Date(now.getTime() - h * 3600000).toISOString();
  return [
    { id: 'o1', businessId, tableNumber: '4', status: 'delivered' as const, total: 47.97, createdAt: ago(3), updatedAt: ago(2), items: [] },
    { id: 'o2', businessId, tableNumber: '7', status: 'delivered' as const, total: 43.98, createdAt: ago(8), updatedAt: ago(7), items: [] },
  ].filter((o) => !since || o.createdAt >= since);
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
